<template>
  <b-modal
    ref="modal"
    :title="$t('influencer_panel.material_title')"
    :ok-only="true"
    :ok-title="$t('action.close')"
    ok-variant="outline-secondary"
    size="lg"
    @hidden="reset"
  >
    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-skeleton width="60%" />
        <b-skeleton width="90%" />
        <b-skeleton width="80%" />
      </template>

      <section-error v-if="error" :message="error" @retry="load" />

      <template v-else-if="material">
        <p class="material__campaign">{{ campaignName(current) }}</p>

        <div v-if="material.briefing" class="material__block">
          <div class="material__label">{{ $t('influencer_panel.briefing') }}</div>
          <p class="material__text">{{ material.briefing }}</p>
        </div>

        <div v-if="material.rules" class="material__block">
          <div class="material__label">{{ $t('influencer_panel.rules') }}</div>
          <p class="material__text">{{ material.rules }}</p>
        </div>

        <div v-if="hashtags.length" class="material__block">
          <div class="material__label">{{ $t('influencer_panel.hashtags') }}</div>
          <div class="material__chips">
            <b-badge v-for="tag in hashtags" :key="tag" variant="light-primary">#{{ tag }}</b-badge>
          </div>
        </div>

        <div v-if="material.disclosure_tag" class="material__block">
          <div class="material__label">{{ $t('influencer_panel.disclosure_tag') }}</div>
          <b-badge variant="light-warning">{{ material.disclosure_tag }}</b-badge>
        </div>

        <div v-if="assets.length" class="material__block">
          <div class="material__label">{{ $t('influencer_panel.material') }}</div>
          <div class="material__assets">
            <a
              v-for="asset in assets"
              :key="asset.id"
              :href="asset.url"
              target="_blank"
              rel="noopener"
              class="material__asset"
            >
              <feather-icon icon="ImageIcon" size="15" />
              <span>{{ asset.name || asset.file_name || `#${asset.id}` }}</span>
              <feather-icon icon="ExternalLinkIcon" size="13" />
            </a>
          </div>
        </div>

        <hb-empty-state v-if="isEmpty" :title="$t('influencer_panel.no_material')" icon="ImageIcon" />
      </template>
    </b-skeleton-wrapper>
  </b-modal>
</template>

<script>
import {
  BModal, BBadge, BSkeleton, BSkeletonWrapper,
} from 'bootstrap-vue'
import InfluencerPanelService from '@/services/api/InfluencerPanelService'
import HbEmptyState from '@/components/hubee/HbEmptyState.vue'
import SectionError from './SectionError.vue'
import { campaignName } from '../campaign'
import { extractMessage } from '../panel'

/**
 * Material de divulgacao da campanha: briefing, regras, hashtags e as pecas.
 *
 * Busca so ao abrir — sao dados que a maioria das sessoes nunca olha, e carrega-los junto
 * da lista multiplicaria as consultas por campanha.
 */
export default {
  name: 'CampaignMaterialModal',
  components: {
    BModal, BBadge, BSkeleton, BSkeletonWrapper, HbEmptyState, SectionError,
  },
  props: {
    participation: { type: Object, default: null },
  },
  data: () => ({
    service: null,
    target: null,
    material: null,
    loading: false,
    error: '',
  }),
  computed: {
    /**
     * A participacao chega por argumento no open() porque o pai seta a prop e
     * abre no mesmo tick — a prop ainda nao propagou, e o primeiro clique abria
     * a modal vazia (o load() desistia sem participacao).
     */
    current() {
      return this.target || this.participation
    },
    hashtags() {
      const raw = (this.material && this.material.hashtags) || []

      return Array.isArray(raw) ? raw : String(raw).split(/[\s,]+/).filter(Boolean)
    },
    assets() {
      return (this.material && this.material.assets) || []
    },
    isEmpty() {
      return !!this.material
        && !this.material.briefing && !this.material.rules
        && !this.hashtags.length && !this.material.disclosure_tag && !this.assets.length
    },
  },
  beforeMount() {
    this.service = InfluencerPanelService.build(this.$vs)
  },
  methods: {
    campaignName,
    open(participation = null) {
      this.target = participation || this.participation
      this.$refs.modal.show()
      this.load()
    },
    reset() {
      this.target = null
      this.material = null
      this.error = ''
    },
    load() {
      if (!this.current) return

      this.loading = true
      this.error = ''

      this.service.campaignAssets(this.current.id)
        .then(material => { this.material = material })
        .catch(error => { this.error = extractMessage(error) })
        .finally(() => { this.loading = false })
    },
  },
}
</script>

<style scoped>
.material__campaign {
  margin: 0 0 1rem;
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.material__block {
  margin-bottom: 1.1rem;
}

.material__label {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
  margin-bottom: 0.35rem;
}

.material__text {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text-body);
  white-space: pre-line;
}

.material__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.material__assets {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.material__asset {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--surface-sunken);
  border-radius: var(--radius);
  font-size: var(--fs-sm);
}
</style>
